import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { AppLink } from "@/components/app-link";
import { PageHeader, PageHeaderSkeleton } from "@/components/layout";
import { RevisionBadge } from "@/components/semantic-badges";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRouteParam } from "@/lib/use-route-param";

export const Route = createFileRoute("/revCollab/documents/$documentId")({
  component: RevCollabDocumentPage,
});

export function RevCollabDocumentPage() {
  const documentId = useRouteParam("documentId");
  const { data: details } = useQuery(
    convexQuery(
      api.revCollab.getHubDocument,
      documentId ? { documentId: documentId as Id<"projectDocuments"> } : "skip"
    )
  );
  return (
    <div className="space-y-4">
      {details === undefined ? (
        <PageHeaderSkeleton withTrail />
      ) : (
        <PageHeader
          title={details?.document?.name ?? "CollabPilot Document"}
          trail={[{ label: "CollabPilot", href: "/revCollab" }]}
        />
      )}

      {details === undefined ? (
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">Loading revisions...</CardContent>
        </Card>
      ) : details === null || details.revisions.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-sm text-muted-foreground">
            No PDF/image revisions available for collaboration.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {details.revisions.map((revision) => (
            <Card key={revision._id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{revision.revisionName}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center justify-between gap-3">
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p className="flex min-w-0 items-center gap-2">
                    <RevisionBadge number={revision.revisionNumber} />
                    <span className="truncate">{revision.originalFilename}</span>
                  </p>
                  <p>
                    {revision.hasSession ? (
                      <Badge variant="secondary" size="sm">Active History</Badge>
                    ) : (
                      <Badge variant="outline" size="sm">No Session Yet</Badge>
                    )}
                  </p>
                </div>
                <AppLink
                  to="/revCollab/revisions/$revisionId"
                  params={{ revisionId: revision._id }}
                  title={`CollabPilot · ${details.document.name} r${revision.revisionNumber}`}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Open session
                </AppLink>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
